import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Cigarette, Users, Heart, Brain } from 'lucide-react';
import CTA from '../../components/CTA';

const NewtownabbeyQuitSmoking: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Quit Smoking Hypnotherapy Newtownabbey | Family Health Focus</title>
        <meta
          name="description"
          content="Family-focused quit smoking hypnotherapy serving Newtownabbey from our south Belfast clinic on the Ormeau Road. Intensive 2-hour session. Role model for children. 60-80% success."
        />
        <meta name="keywords" content="quit smoking Newtownabbey, stop smoking Newtownabbey, family health hypnotherapy Newtownabbey, quit vaping Newtownabbey, parenting motivation" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/newtownabbey-quit-smoking/" />
        <meta property="og:title" content="Quit Smoking Hypnotherapy Newtownabbey | Family Health Focus" />
        <meta property="og:description" content="Family-focused quit smoking hypnotherapy serving Newtownabbey. Role model for children. Intensive 2-hour session." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Smoking Cessation Newtownabbey",
            "description": "CNHC registered hypnotherapy for smoking cessation, serving Newtownabbey from Belfast clinic. Intensive 2-hour session with lifetime support and money-back guarantee. Family health focus for parents.",
            "url": "https://www.sharonclare-hypnotherapy.com/newtownabbey-quit-smoking/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Smoking Cessation Hypnotherapy",
            "areaServed": {
              "@type": "City",
              "name": "Newtownabbey",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic (Ormeau Road)",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "addressRegion": "County Antrim",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How does quitting smoking help me be a better role model for my children?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Children learn by watching their parents. If you smoke, your children are significantly more likely to become smokers themselves, even if you tell them not to. Quitting smoking demonstrates that change is possible, models healthy behavior, and protects your children from secondhand smoke exposure (which increases their risk of respiratory infections, asthma, and ear infections). Many Newtownabbey parents are motivated by wanting to be present and healthy for their children's milestones - school events, sports, graduations, weddings. The intensive quit smoking session addresses these family motivations specifically."
                }
              },
              {
                "@type": "Question",
                "name": "What about secondhand smoke risk to my children?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Secondhand smoke is a serious health risk for children. It increases risk of respiratory infections, asthma attacks, ear infections, and SIDS in infants. Even if you smoke outside, residue clings to clothes, hair, and skin (called thirdhand smoke), which children then breathe in when you hug them or sit near them. Children's lungs are still developing, making them particularly vulnerable. For Newtownabbey parents, quitting smoking isn't just about your own health - it's about protecting your children's health and creating a smoke-free home environment."
                }
              },
              {
                "@type": "Question",
                "name": "Can I schedule sessions around school hours in Newtownabbey?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, absolutely. Clinic days and times vary, and early evening appointments are available - current availability and online booking are on the clinic's own page (https://belfastchiropracticclinic.janeapp.co.uk/#/staff_member/37). Many Newtownabbey parents schedule daytime sessions during school hours so they don't need childcare. The clinic is a short drive from Newtownabbey via the M2 and the Ormeau Road, making it easy to attend a morning or afternoon session and be back before school pickup, and early evening slots suit parents who work during the day."
                }
              },
              {
                "@type": "Question",
                "name": "How close is the Belfast clinic to Newtownabbey?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The Belfast clinic is at 228 Ormeau Road in south Belfast - take the M2 into the city, then head south along the Ormeau Road (A24). For most Newtownabbey addresses that is a straightforward drive of under half an hour outside peak traffic, which is ideal for busy parents fitting appointments around school runs, work, and family commitments. It's close enough that you can attend a session during the day and be back home without disrupting your family routine."
                }
              },
              {
                "@type": "Question",
                "name": "What if my children see me struggle to quit?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "One of the benefits of hypnotherapy is that it significantly reduces the irritability, mood swings, and cravings typically associated with quitting smoking. Many clients report they don't experience obvious withdrawal symptoms that would affect their parenting or family interactions. This makes it much easier to quit discreetly without your children witnessing struggle. Additionally, if your children do notice, it's actually valuable for them to see that change is possible and that their parent cares enough about health and family to make positive changes."
                }
              },
              {
                "@type": "Question",
                "name": "Is there evening availability for working parents in Newtownabbey?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, early evening appointments are available - clinic days and times vary, so current availability and online booking are on the clinic's own page (https://belfastchiropracticclinic.janeapp.co.uk/#/staff_member/37). For dual-income Newtownabbey families where both parents work, early evening sessions provide flexibility to attend without taking time off work. The short drive from Newtownabbey makes them particularly convenient - you can finish work, attend your session, and be home without a long commute affecting family time."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Family-Focused Quit Smoking Hypnotherapy for Newtownabbey
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Be the healthy role model your children deserve
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">M2 into Belfast</span> from Newtownabbey • <span className="font-semibold">short drive</span> to the Ormeau Road
                </p>
              </div>
              <p className="text-gray-600 mt-2">School hours scheduling • Evening appointments available</p>
            </div>
            <div className="bg-white rounded-xl p-6 mb-8 max-w-2xl mx-auto">
              <div className="flex items-start space-x-4">
                <Users className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div className="text-left">
                  <p className="text-lg font-semibold text-gray-900 mb-2">Invest in Your Family's Health</p>
                  <p className="text-gray-700">
                    Intensive 2-hour session with lifetime support + money-back guarantee
                  </p>
                  <p className="text-accent-700 font-medium mt-1">Protect children from secondhand smoke • Be present for their milestones • Model healthy behavior</p>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Quit Smoking for Your Family in Newtownabbey
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                If you're a parent in Newtownabbey ready to quit smoking for your family's health, professional support is conveniently close. I serve Newtownabbey clients from my Belfast clinic at 228 Ormeau Road in south Belfast. The journey is a straightforward run down the M2 into the city and then south along the Ormeau Road (A24), making this a convenient location for busy Newtownabbey parents who need to fit appointments around school runs, work, and family commitments.
              </p>
              <p>
                You already know you should quit smoking. You've read the health warnings, seen the cost mounting up, heard your children ask why you smoke. But knowing you should quit and actually quitting are two very different things. The guilt is real - guilt about exposing your children to secondhand smoke, guilt about the money spent on cigarettes that could go to family expenses, guilt about setting a bad example while telling them to make healthy choices.
              </p>
              <p>
                Here's the truth that might bring some relief: parenting motivation is one of the strongest drivers of successful smoking cessation. Parents who want to quit for their children have higher success rates because the emotional connection is so powerful. You're not just quitting for yourself - you're quitting to protect your children's health, to be present for their milestones, to model that change is possible, and to create a smoke-free home environment.
              </p>
              <p>
                Children learn by watching their parents. Research shows that children of smokers are significantly more likely to become smokers themselves, even when parents explicitly tell them not to smoke. Your behavior speaks louder than your words. By quitting smoking, you're not just protecting your own health - you're potentially preventing your children from ever starting. You're demonstrating resilience, self-care, and the courage to change unhealthy patterns.
              </p>
              <p>
                Solution-focused hypnotherapy works by rewiring the subconscious habits that keep you smoking. Instead of relying on willpower alone (which is already depleted from managing work, children, household responsibilities), we work with your brain's autopilot patterns to naturally make you a non-smoker. The intensive 2-hour session includes lifetime support and a money-back guarantee. For Newtownabbey families, this investment in health pays for itself within weeks while protecting your children's present and future wellbeing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Family Health Focus */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Protecting Your Family's Health
            </h2>
            <div className="space-y-8">
              <div className="bg-white rounded-xl p-8">
                <div className="flex items-start space-x-4">
                  <Heart className="w-10 h-10 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                      Secondhand Smoke Risk to Children
                    </h3>
                    <p className="text-gray-700 mb-4">
                      Even if you smoke outside or away from your children, secondhand smoke still affects them:
                    </p>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Respiratory infections:</strong> Children exposed to secondhand smoke get more colds, bronchitis, and pneumonia</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Asthma:</strong> Secondhand smoke triggers asthma attacks and worsens symptoms in children with asthma</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Ear infections:</strong> Children of smokers get more ear infections requiring antibiotics and potentially tubes</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Thirdhand smoke:</strong> Smoke residue clings to clothes, hair, furniture, car seats - children breathe this in when they hug you or sit near you</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Long-term effects:</strong> Childhood exposure increases risk of becoming smokers themselves and developing lung problems as adults</span>
                      </li>
                    </ul>
                    <p className="text-gray-700 mt-4">
                      For Newtownabbey parents, quitting smoking means creating a genuinely smoke-free home environment where your children can grow up healthy without the invisible burden of secondhand smoke.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8">
                <div className="flex items-start space-x-4">
                  <Users className="w-10 h-10 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                      Being the Role Model Your Children Deserve
                    </h3>
                    <p className="text-gray-700 mb-4">
                      Children learn more from what you do than what you say. When you quit smoking:
                    </p>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Break the cycle:</strong> Children of smokers are 3x more likely to become smokers - quitting reduces this risk dramatically</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Model healthy behavior:</strong> Show your children that you prioritize health and make positive changes</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Demonstrate resilience:</strong> Quitting smoking shows that difficult changes are possible with commitment</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Avoid hypocrisy:</strong> No longer telling children to make healthy choices while smoking yourself</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Physical presence:</strong> Better health means you can be active with your children - play sports, go for walks, attend school events without breathlessness</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8">
                <div className="flex items-start space-x-4">
                  <Heart className="w-10 h-10 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                      Financial Benefits for Newtownabbey Families
                    </h3>
                    <p className="text-gray-700 mb-4">
                      The money you're spending on cigarettes could transform your family's financial situation:
                    </p>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Every month:</strong> What a 20-a-day habit costs - that's school uniforms, activities, family outings</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Every year:</strong> Could be family holiday, emergency savings, children's education fund</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>The session:</strong> Pays for itself in just 3 weeks of not smoking</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Long-term security:</strong> Money redirected to family needs, children's futures, quality of life improvements</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              How the Intensive Session Works for Parents
            </h2>
            <div className="space-y-8">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Addressing Parenting-Specific Triggers
                </h3>
                <p className="text-gray-700 mb-4">
                  Parents often have unique smoking triggers that the intensive 2-hour session specifically addresses:
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Stress relief:</strong> Managing work, children, household - cigarettes feel like your only break</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Brief escape:</strong> Smoking gives you a few minutes alone when parenting feels overwhelming</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Evening habit:</strong> Cigarette after children are in bed feels like a reward for getting through the day</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Social situations:</strong> School gate conversations, coffee with other parents who smoke</span>
                  </li>
                </ul>
                <p className="text-gray-700 mt-4">
                  We identify your specific parenting smoking patterns and use hypnotherapy to rewire these subconscious habits. You'll learn alternative stress management strategies that work for busy parents without relying on cigarettes.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Convenient for Busy Newtownabbey Parents
                </h3>
                <p className="text-gray-700 mb-4">
                  The session is designed to fit around family commitments:
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Close by:</strong> A short drive from Newtownabbey via the M2 and Ormeau Road - easy to fit between school drop-off and pickup</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>School hours scheduling:</strong> Daytime appointments can often be arranged while the children are at school</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Evening appointments:</strong> Early evening slots are available for working parents</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Single intensive session:</strong> Most clients need just one 2-hour session - no long-term commitment affecting family schedule</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Reduced withdrawal symptoms:</strong> Hypnotherapy approach minimizes irritability so you can parent effectively while quitting</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Newtownabbey Parents Say
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "My 8-year-old asked me why I smoked when I told her it was bad for her. I had no good answer. That guilt drove me to Sharon's clinic - just 10 minutes from Newtownabbey, so convenient. Three months smoke-free and my daughter is so proud of me. I'm proud of myself too. The money I'm saving is going to her swimming lessons."
                </p>
                <p className="font-semibold text-gray-900">K.M., Newtownabbey - Parent motivation</p>
              </div>

              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "My son has asthma and I knew secondhand smoke was making it worse, even though I only smoked outside. The guilt was eating me up. One intensive session during school hours and I quit. His asthma symptoms have improved, and I'm not constantly worried about affecting his health anymore."
                </p>
                <p className="font-semibold text-gray-900">D.P., Rathcoole - Child health motivation</p>
              </div>

              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Both my husband and I smoked. We realized we were setting a terrible example for our teenage daughter. We did Sharon's program together (separate sessions). Six months smoke-free for both of us now. The money we were spending on cigarettes every month is now going into a holiday fund. Our daughter says she's relieved we quit."
                </p>
                <p className="font-semibold text-gray-900">T.R., Glengormley - Family transformation</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Newtownabbey Quit Smoking
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How does quitting smoking help me be a better role model for my children?
                </h3>
                <p className="text-gray-700">
                  Children learn by watching their parents. If you smoke, your children are significantly more likely to become smokers themselves, even if you tell them not to. Quitting smoking demonstrates that change is possible, models healthy behavior, and protects your children from secondhand smoke exposure (which increases their risk of respiratory infections, asthma, and ear infections). Many Newtownabbey parents are motivated by wanting to be present and healthy for their children's milestones - school events, sports, graduations, weddings. The intensive quit smoking session addresses these family motivations specifically.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  What about secondhand smoke risk to my children?
                </h3>
                <p className="text-gray-700">
                  Secondhand smoke is a serious health risk for children. It increases risk of respiratory infections, asthma attacks, ear infections, and SIDS in infants. Even if you smoke outside, residue clings to clothes, hair, and skin (called thirdhand smoke), which children then breathe in when you hug them or sit near them. Children's lungs are still developing, making them particularly vulnerable. For Newtownabbey parents, quitting smoking isn't just about your own health - it's about protecting your children's health and creating a smoke-free home environment.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can I schedule sessions around school hours in Newtownabbey?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. Clinic days and times vary, and early evening appointments are available - current availability and online booking are on the{' '}
                  <a
                    href="https://belfastchiropracticclinic.janeapp.co.uk/#/staff_member/37"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-600 font-semibold hover:underline"
                  >
                    clinic's own booking page
                  </a>
                  . Many Newtownabbey parents schedule daytime sessions during school hours so they don't need childcare. The clinic is a short drive from Newtownabbey via the M2 and the Ormeau Road, making it easy to attend a morning or afternoon session and be back before school pickup, and early evening slots suit parents who work during the day.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How close is the Belfast clinic to Newtownabbey?
                </h3>
                <p className="text-gray-700">
                  The Belfast clinic is at 228 Ormeau Road in south Belfast - take the M2 into the city, then head south along the Ormeau Road (A24). For most Newtownabbey addresses that is a straightforward drive of under half an hour outside peak traffic, which is ideal for busy parents fitting appointments around school runs, work, and family commitments. It's close enough that you can attend a session during the day and be back home without disrupting your family routine.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  What if my children see me struggle to quit?
                </h3>
                <p className="text-gray-700">
                  One of the benefits of hypnotherapy is that it significantly reduces the irritability, mood swings, and cravings typically associated with quitting smoking. Many clients report they don't experience obvious withdrawal symptoms that would affect their parenting or family interactions. This makes it much easier to quit discreetly without your children witnessing struggle. Additionally, if your children do notice, it's actually valuable for them to see that change is possible and that their parent cares enough about health and family to make positive changes.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is there evening availability for working parents in Newtownabbey?
                </h3>
                <p className="text-gray-700">
                  Yes, early evening appointments are available - clinic days and times vary, so current availability and online booking are on the{' '}
                  <a
                    href="https://belfastchiropracticclinic.janeapp.co.uk/#/staff_member/37"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-600 font-semibold hover:underline"
                  >
                    clinic's own booking page
                  </a>
                  . For dual-income Newtownabbey families where both parents work, early evening sessions provide flexibility to attend without taking time off work. The short drive from Newtownabbey makes them particularly convenient - you can finish work, attend your session, and be home without a long commute affecting family time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />
    </main>
  );
};

export default NewtownabbeyQuitSmoking;
